/**
 * Come back where you left off, in the native app.
 *
 * iOS and Android reclaim memory by throwing away a backgrounded app — or just
 * its webview process. Coming back then starts the SPA from scratch at `/`,
 * which redirects to a new chat, and whatever was typed but not sent is gone.
 * Switching to another app to copy a paragraph and returning to an empty new
 * chat was the single most-reported annoyance from mobile testers (KAN-547).
 *
 * So: when the app goes to the background, write down which chat was open and
 * what was typed into it. On the next cold start, `/` restores that instead of
 * opening a new chat, and the composer gets its text back.
 *
 * Two deliberate limits:
 *
 *  - **Native only.** In a browser the tab survives, and a shared
 *    `localStorage` across tabs would have two windows fighting over one
 *    remembered chat. `isNativeShell()` gates every entry point, so on the web
 *    every function here is a no-op.
 *  - **It expires.** A chat from two days ago is not where you want to land,
 *    and a two-day-old half-sentence is not what you meant to send. After
 *    `SESSION_RESTORE_WINDOW_MS` the app starts fresh, as it does today.
 */

import { peekComposerText } from '@/lib/composer-draft';
import { isNativeShell } from '@/lib/native/platform';
import { useChatStore } from '@/lib/stores/chat.store';

const STORAGE_KEY = 'ninebrains.session-restore';

/**
 * When this module loaded — for the app, when it started.
 *
 * Restoring is a cold-start affair. Without this bound, the draft came back
 * every time a composer mounted inside the same session: type half a message,
 * switch apps, come back, tap "new chat" to write something else, and the old
 * text was waiting there. Fifteen seconds covers a cold start's chat load with
 * room to spare and is over long before a user gets anywhere by hand.
 */
const BOOTED_AT = Date.now();
export const BOOT_RESTORE_WINDOW_MS = 15_000;

/**
 * How long a remembered chat and its unsent text stay valid: eight hours.
 * Long enough to cover a lunch break or an afternoon of interruptions, short
 * enough that the next morning starts on a clean new chat.
 */
export const SESSION_RESTORE_WINDOW_MS = 8 * 60 * 60 * 1000;

export type SessionSnapshot = {
  /** Backend id of the open chat, or `null` for an unsent new chat. */
  chatId: string | null;
  /** Text sitting unsent in that chat's composer (`''` when empty). */
  draft: string;
  /** When this snapshot was written, ms since epoch. */
  at: number;
};

/** Within the restore window — the only reason a snapshot is worth applying. */
export function isSnapshotFresh(
  snapshot: SessionSnapshot | null,
  now: number,
  windowMs: number = SESSION_RESTORE_WINDOW_MS
): boolean {
  if (!snapshot) return false;
  const age = now - snapshot.at;
  // A clock that moved backwards (timezone change, NTP correction) makes `age`
  // negative. Treat that as fresh rather than throwing the snapshot away.
  return age < windowMs;
}

/** The chat a snapshot restores to, or `null` when it restores to nothing. */
export function restoreChatIdFor(snapshot: SessionSnapshot | null): string | null {
  return snapshot?.chatId ?? null;
}

/**
 * The remembered text for the composer of `sessionId`, or `''`.
 *
 * An unsaved chat has no id worth matching on: it carries a locally generated
 * one that is different on every start (and comes in two shapes, `draft-…` and
 * assistant-ui's `__LOCALID_…`). So "the text belonged to an unsaved chat" is
 * matched against "this composer sits in an unsaved chat", which the chat store
 * answers directly with `isDraft`.
 */
export function draftFor(
  snapshot: SessionSnapshot | null,
  sessionId: string | null | undefined,
  isUnsavedChat: boolean
): string {
  if (!snapshot?.draft || !sessionId) return '';
  if (snapshot.chatId === null) return isUnsavedChat ? snapshot.draft : '';
  return snapshot.chatId === sessionId ? snapshot.draft : '';
}

function read(): SessionSnapshot | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SessionSnapshot>;
    if (typeof parsed?.at !== 'number') return null;
    return {
      chatId: typeof parsed.chatId === 'string' ? parsed.chatId : null,
      draft: typeof parsed.draft === 'string' ? parsed.draft : '',
      at: parsed.at,
    };
  } catch {
    // Storage disabled or the entry is corrupt — start fresh.
    return null;
  }
}

function write(snapshot: SessionSnapshot | null): void {
  try {
    if (!snapshot) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // Storage full or disabled — restoring is a convenience, never a promise.
  }
}

/**
 * Write down the current chat and its unsent text.
 *
 * Called when the app is hidden, which is the only moment the OS can kill it
 * without warning, so one write per app switch instead of one per keystroke.
 * A new chat with an empty composer is worth nothing on the way back and
 * clears the entry instead of parking an empty one.
 */
export function captureSession(now: number = Date.now()): void {
  const { activeChatId, currentChat } = useChatStore.getState();
  const draft = peekComposerText(activeChatId);
  // An unsaved chat exists only in this webview — there is no id to come back
  // to, only the text that was typed into it.
  const isUnsaved = !!currentChat?.isDraft && currentChat.id === activeChatId;
  const chatId = isUnsaved ? null : activeChatId;
  if (!chatId && !draft) {
    write(null);
    return;
  }
  write({ chatId, draft, at: now });
}

/** The chat this start was sent into by the restore, if any. */
let restoredChatId: string | null = null;

/**
 * The draft that was put back into a composer, and the chat it belongs to.
 *
 * Module state, not component state: the composer is remounted while a chat
 * opens, and the note has to outlive that. See `restoredDraftBelongsElsewhere`.
 */
let placedDraft: { chatId: string; text: string } | null = null;

/** Note that `text` was put back into the composer of `chatId`. */
export function noteRestoredDraft(chatId: string, text: string): void {
  placedDraft = { chatId, text };
}

/**
 * True when the draft that was put back is now sitting in a fresh, unsent chat
 * it was not written in — and should be taken back out of it.
 *
 * A restored draft belongs to the chat it was written in. Text written into the
 * composer does not behave like typed text when the user then opens another
 * chat: typed text stays behind, this one comes along. So tapping "new chat" to
 * write something else started with the old half-sentence already in the box.
 *
 * Two limits, both learned the hard way:
 *
 *  - **Only into an unsent chat.** A fresh chat is its own local thread, so
 *    clearing there touches nothing else. Clearing it in another *saved* chat
 *    reached back into the home chat's composer and wiped the draft there too —
 *    losing exactly the half-sentence this whole feature exists to keep.
 *  - **Only while untouched.** Once the user has edited it, the text is theirs
 *    and stays wherever they put it.
 */
export function restoredDraftBelongsElsewhere(
  currentChatId: string | null | undefined,
  currentText: string,
  isUnsavedChat: boolean
): boolean {
  if (!placedDraft || !currentChatId || !isUnsavedChat) return false;
  return placedDraft.chatId !== currentChatId && placedDraft.text === currentText;
}

/** Forget the note — the draft has been dealt with. */
export function forgetRestoredDraft(): void {
  placedDraft = null;
}

/**
 * The chat the app should open on a cold start, or `null` to keep today's
 * behaviour (a new chat). Leaves the snapshot in place — the composer still
 * needs its draft out of it.
 */
export function readRestoreChatId(now: number = Date.now()): string | null {
  if (!isNativeShell()) return null;
  const snapshot = read();
  if (!isSnapshotFresh(snapshot, now)) return null;
  restoredChatId = restoreChatIdFor(snapshot);
  return restoredChatId;
}

/**
 * True for the chat this start was restored into. The chat may be gone — the
 * user deleted it on their desktop, or it belongs to a tenant this start is
 * not in — and then the app would land on "conversation not found" on every
 * single start for the next eight hours. Whoever sees that failure calls
 * `clearSessionRestore()` and sends the user to a new chat instead.
 */
export function isRestoredChat(chatId: string | null | undefined): boolean {
  return !!chatId && chatId === restoredChatId;
}

/**
 * The remembered composer text for `sessionId`, or `''`.
 *
 * A look, not a take: the composer this belongs to may not be mounted yet when
 * the first caller asks, and spending the draft on a call that cannot deliver
 * it would lose exactly what this feature exists to keep. `consumeDraft()`
 * spends it, once it is actually in the composer.
 */
export function peekRestorableDraft(
  sessionId: string | null | undefined,
  isUnsavedChat: boolean,
  now: number = Date.now()
): string {
  if (!isNativeShell()) return '';
  if (now - BOOTED_AT > BOOT_RESTORE_WINDOW_MS) return '';
  const snapshot = read();
  if (!isSnapshotFresh(snapshot, now)) return '';
  return draftFor(snapshot, sessionId, isUnsavedChat);
}

/**
 * Drop the remembered text now that it is back in the composer. Restoring is a
 * one-shot: a later visit to the same chat starts empty, and a send is never
 * shadowed by a stale draft reappearing. The remembered chat itself stays.
 */
export function consumeRestorableDraft(): void {
  const snapshot = read();
  if (!snapshot?.draft) return;
  write({ ...snapshot, draft: '' });
}

/**
 * Forget everything remembered. Called when the restored chat turns out not to
 * be openable any more — deleted on another device, or belonging to a tenant
 * this start is not in — so the next start does not walk into the same dead
 * end, and on sign-out, so nothing of one person survives into the next
 * person's session on a shared device.
 */
export function clearSessionRestore(): void {
  write(null);
}

let armed = false;

/**
 * Start remembering. Called once from `main.tsx` in the native shell.
 *
 * `visibilitychange` is what fires when the app is backgrounded in both the
 * iOS and the Android webview; `pagehide` covers the webview being torn down
 * while still visible (an OTA bundle swap, a process recycle).
 */
export function initSessionRestore(): void {
  if (armed || !isNativeShell()) return;
  if (typeof document === 'undefined') return;
  armed = true;

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') captureSession();
  });
  window.addEventListener('pagehide', () => captureSession());
}
